import { useTranslation } from "react-i18next";
import { ShieldCheck, Package, Sparkles, UserPen, UtensilsCrossed } from "lucide-react";
import {
  getFoodProvenance,
  provenanceFromSource,
  confidenceBand,
  type ProvenanceFood,
  type ProvenanceKind,
} from "@workspace/food-provenance";

type Size = "xs" | "sm";

const KIND_META: Record<
  ProvenanceKind,
  { icon: typeof ShieldCheck; labelKey: string; tipKey: string; cls: string }
> = {
  usda: {
    icon: ShieldCheck,
    labelKey: "trust.sourceUsda",
    tipKey: "trust.sourceUsdaTooltip",
    cls: "text-emerald-400 bg-emerald-500/10 border-emerald-500/25",
  },
  branded: {
    icon: Package,
    labelKey: "trust.sourceBranded",
    tipKey: "trust.sourceBrandedTooltip",
    cls: "text-sky-400 bg-sky-500/10 border-sky-500/25",
  },
  restaurant: {
    icon: UtensilsCrossed,
    labelKey: "trust.sourceRestaurant",
    tipKey: "trust.sourceRestaurantTooltip",
    cls: "text-orange-400 bg-orange-500/10 border-orange-500/25",
  },
  ai_estimated: {
    icon: Sparkles,
    labelKey: "trust.sourceAiEstimated",
    tipKey: "trust.sourceAiEstimatedTooltip",
    cls: "text-violet-400 bg-violet-500/10 border-violet-500/25",
  },
  user: {
    icon: UserPen,
    labelKey: "trust.sourceUser",
    tipKey: "trust.sourceUserTooltip",
    cls: "text-amber-400 bg-amber-500/10 border-amber-500/25",
  },
};

const SIZE_CLS: Record<Size, { wrap: string; icon: string }> = {
  xs: { wrap: "text-[9px] px-1.5 py-0.5 gap-0.5", icon: "w-2.5 h-2.5" },
  sm: { wrap: "text-[10px] px-2 py-0.5 gap-1", icon: "w-3 h-3" },
};

/**
 * Small pill that labels where a food's nutrient data came from
 * (USDA / Branded / AI-estimated). For AI estimates it can also show the
 * model's confidence so users can tell verified data from inference.
 */
export function SourceBadge({
  food,
  source,
  confidence,
  size = "xs",
  showConfidence = true,
  className = "",
}: {
  food?: ProvenanceFood | null;
  /** API provenance label (food-search / barcode-lookup). Takes precedence. */
  source?: string | null;
  confidence?: number | null;
  size?: Size;
  showConfidence?: boolean;
  className?: string;
}) {
  const { t } = useTranslation();
  const prov =
    provenanceFromSource(source, { confidence }) ??
    getFoodProvenance(food, { confidence });
  const meta = KIND_META[prov.kind];
  const Icon = meta.icon;
  const sz = SIZE_CLS[size];

  const showPct =
    showConfidence &&
    prov.kind === "ai_estimated" &&
    typeof prov.confidence === "number";
  const pct = showPct ? Math.round((prov.confidence as number) * 100) : null;

  return (
    <span
      className={`inline-flex items-center rounded-full border font-medium shrink-0 ${meta.cls} ${sz.wrap} ${className}`}
      title={t(meta.tipKey)}
      data-testid={`source-badge-${prov.kind}`}
    >
      <Icon className={sz.icon} />
      {t(meta.labelKey)}
      {pct != null && <span className="font-mono ml-0.5">· {pct}%</span>}
    </span>
  );
}

/**
 * Standalone confidence cue for AI-estimated values (0..1). Renders three
 * bands as filled bars plus a percentage, color-coded by band.
 */
export function ConfidenceMeter({
  value,
  size = "sm",
  className = "",
}: {
  value: number;
  size?: Size;
  className?: string;
}) {
  const { t } = useTranslation();
  const band = confidenceBand(value);
  const pct = Math.round(value * 100);
  const color =
    band === "high"
      ? "text-emerald-400"
      : band === "medium"
        ? "text-amber-400"
        : "text-orange-400";
  const barColor =
    band === "high"
      ? "bg-emerald-400"
      : band === "medium"
        ? "bg-amber-400"
        : "bg-orange-400";
  const filled = band === "high" ? 3 : band === "medium" ? 2 : 1;
  const txt = size === "xs" ? "text-[9px]" : "text-[10px]";

  return (
    <span
      className={`inline-flex items-center gap-1 ${txt} ${className}`}
      title={t(`trust.confidence${band[0].toUpperCase()}${band.slice(1)}`)}
      data-testid="confidence-meter"
    >
      <span className="flex items-end gap-0.5" aria-hidden="true">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className={`w-1 rounded-sm ${i < filled ? barColor : "bg-muted/40"}`}
            style={{ height: `${4 + i * 2}px` }}
          />
        ))}
      </span>
      <span className={`font-mono font-bold ${color}`}>{pct}%</span>
    </span>
  );
}
